import { deflateRaw, inflateRaw } from 'pako';
import localStorageRepositories from '@/plugins/localStorageRepositories';

export const CROSS_DOMAIN_CV_PARAMS = {
  payload: 'storeeel_cv',
};

export const STOREEEL_CV_MAX_LEN = 2500;

const CROSS_DOMAIN_CV_LOG_KEYS = [
  'campaignClick',
  'groupClick',
  'mediaView',
  'ctaClick',
];
const normalizeOptionalValue = (value) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value);
  return normalized === '' ? null : normalized;
};

const hasOwn = (target, key) => (
  target && Object.prototype.hasOwnProperty.call(target, key)
);

const isPlainObject = (value) => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
);

const getTimestamp = (value) => {
  if (!isPlainObject(value)) return null;
  const candidates = [
    value.timestamp,
    value.createdAt,
    value.created_at,
    value.updatedAt,
    value.updated_at,
  ];

  for (const candidate of candidates) {
    const timestamp = Number(candidate);
    if (Number.isFinite(timestamp)) return timestamp;
  }

  return null;
};

const newestFirst = (items) => (
  [...items].sort((a, b) => {
    if (a.timestamp !== null && b.timestamp !== null) return b.timestamp - a.timestamp;
    if (a.timestamp !== null) return -1;
    if (b.timestamp !== null) return 1;
    return b.index - a.index;
  })
);

const stringToUtf8Bytes = (input) => {
  if (typeof globalThis.TextEncoder === 'function') {
    return new globalThis.TextEncoder().encode(input);
  }

  const encoded = encodeURIComponent(input);
  const bytes = [];
  for (let i = 0; i < encoded.length; i += 1) {
    if (encoded[i] === '%') {
      bytes.push(Number.parseInt(encoded.slice(i + 1, i + 3), 16));
      i += 2;
    } else {
      bytes.push(encoded.charCodeAt(i));
    }
  }
  return Uint8Array.from(bytes);
};

const utf8BytesToString = (bytes) => {
  if (typeof globalThis.TextDecoder === 'function') {
    return new globalThis.TextDecoder().decode(bytes);
  }

  return decodeURIComponent(Array.from(bytes, (byte) => (
    `%${byte.toString(16).padStart(2, '0')}`
  )).join(''));
};

const bytesToBinary = (bytes) => {
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return binary;
};

const binaryToBytes = (binary) => (
  Uint8Array.from(binary, (character) => character.charCodeAt(0))
);

const encodeBase64 = (binary) => {
  if (typeof globalThis.btoa === 'function') return globalThis.btoa(binary);
  if (typeof Buffer !== 'undefined') return Buffer.from(binary, 'binary').toString('base64');
  throw new Error('base64 encode is not available');
};

const decodeBase64 = (base64) => {
  if (typeof globalThis.atob === 'function') return globalThis.atob(base64);
  if (typeof Buffer !== 'undefined') return Buffer.from(base64, 'base64').toString('binary');
  throw new Error('base64 decode is not available');
};

export const encodeCrossDomainCvPayload = (payload) => (
  encodeBase64(bytesToBinary(deflateRaw(stringToUtf8Bytes(JSON.stringify(payload)))))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
);

const decodeCompressedJsonPayload = (bytes) => (
  utf8BytesToString(inflateRaw(bytes))
);

const decodePlainJsonPayload = (bytes) => (
  utf8BytesToString(bytes)
);

const expandGroupedMediaLogEntries = (logs) => {
  if (!Array.isArray(logs)) return logs;

  return logs.flatMap((entry) => {
    if (!Array.isArray(entry)) return [];
    const [campaign, group, mediaAssetType, mediaIds] = entry;
    if (!Array.isArray(mediaIds)) return [];
    return mediaIds.map((media) => [media, campaign, group, mediaAssetType]);
  });
};

const normalizeDecodedCrossDomainCvPayload = (payload) => {
  if (!Array.isArray(payload)) return payload;

  const [
    internValues,
    clickedCta,
    campaignLogs,
    groupLogs,
    mediaLogs,
    ctaLogs,
    freqInsertCtaLogs,
    feedFolderCtaLogs,
  ] = payload;
  const normalizedPayload = {
    i: internValues,
    x: clickedCta,
  };
  const logs = {};

  if (Array.isArray(campaignLogs) && campaignLogs.length > 0) logs.c = campaignLogs;
  if (Array.isArray(groupLogs) && groupLogs.length > 0) logs.g = groupLogs;
  if (Array.isArray(mediaLogs) && mediaLogs.length > 0) {
    logs.m = expandGroupedMediaLogEntries(mediaLogs);
  }
  if (Array.isArray(ctaLogs) && ctaLogs.length > 0) {
    logs.a = expandGroupedMediaLogEntries(ctaLogs);
  }
  if (Array.isArray(freqInsertCtaLogs) && freqInsertCtaLogs.length > 0) {
    logs.f = expandGroupedMediaLogEntries(freqInsertCtaLogs);
  }
  if (Array.isArray(feedFolderCtaLogs) && feedFolderCtaLogs.length > 0) {
    logs.k = expandGroupedMediaLogEntries(feedFolderCtaLogs);
  }
  if (Object.keys(logs).length > 0) normalizedPayload.l = logs;

  return normalizedPayload;
};

export const decodeCrossDomainCvPayload = (encodedPayload) => {
  if (!encodedPayload) return null;

  const base64 = String(encodedPayload)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const paddingLength = (4 - (base64.length % 4)) % 4;
  const paddedBase64 = `${base64}${'='.repeat(paddingLength)}`;
  const bytes = binaryToBytes(decodeBase64(paddedBase64));
  let json;
  try {
    json = decodeCompressedJsonPayload(bytes);
  } catch {
    // 移行期間中だけ、DEFLATE導入前のplain JSON payloadも復元できるようにする
    json = decodePlainJsonPayload(bytes);
  }
  return normalizeDecodedCrossDomainCvPayload(JSON.parse(json));
};

const getCrossDomainCvLogs = () => {
  return CROSS_DOMAIN_CV_LOG_KEYS.reduce((acc, keyName) => {
    try {
      const logs = localStorageRepositories.get(keyName)?.getAllValid?.();
      if (logs && typeof logs === 'object' && Object.keys(logs).length > 0) {
        acc[keyName] = logs;
      }
    } catch {
      // localStorage取得に失敗してもCTA遷移自体は継続する
    }
    return acc;
  }, {});
};

const compactCampaignClickLogs = (logs) => {
  const items = Object.entries(logs || {})
    .map(([campaignId, value], index) => ({
      index,
      timestamp: getTimestamp(value),
      campaignId: normalizeOptionalValue(campaignId),
    }))
    .filter((item) => item.campaignId);

  return newestFirst(items).map((item) => item.campaignId);
};

const compactGroupClickLogs = (logs) => {
  const items = [];
  let index = 0;

  Object.entries(logs || {}).forEach(([groupId, campaigns]) => {
    Object.entries(campaigns || {}).forEach(([campaignId, value]) => {
      items.push({
        index,
        timestamp: getTimestamp(value),
        groupId: normalizeOptionalValue(groupId),
        campaignId: normalizeOptionalValue(campaignId),
      });
      index += 1;
    });
  });

  const grouped = [];
  const groupMap = new Map();
  newestFirst(items.filter((item) => item.groupId && item.campaignId))
    .forEach((item) => {
      if (!groupMap.has(item.groupId)) {
        const group = { groupId: item.groupId, campaignIds: [] };
        groupMap.set(item.groupId, group);
        grouped.push(group);
      }
      groupMap.get(item.groupId).campaignIds.push(item.campaignId);
    });

  return grouped.map((item) => [item.groupId, item.campaignIds]);
};

const buildCompactMediaLogItem = ([key, value], index) => {
  const parts = String(key).split('__');
  const mediaId = normalizeOptionalValue(value?.mediaId) || normalizeOptionalValue(parts[0]);
  const campaignId = normalizeOptionalValue(value?.campaignId) || normalizeOptionalValue(parts[1]);
  const groupId = normalizeOptionalValue(
    hasOwn(value, 'groupId') ? value.groupId : (parts[2] ?? null)
  );
  const mediaAssetType = normalizeOptionalValue(value?.mediaAssetType ?? null);

  if (!mediaId || !campaignId) return null;

  return {
    index,
    timestamp: getTimestamp(value),
    entry: [mediaId, campaignId, groupId, mediaAssetType],
  };
};

const compactMediaLogs = (logs) => (
  newestFirst(
    Object.entries(logs || {})
      .map(buildCompactMediaLogItem)
      .filter(Boolean)
  ).map((item) => item.entry)
);

// ctaClick ログを通常コンテンツ(a)・プロモーションコンテンツ(f)・フィードフォルダコンテンツ(k)に振り分ける。
// プロモーション/フィードフォルダは復元時にそれぞれ isFrequencyInsertContent=true / isFeedFolderContent=true
// で保存する必要があるため別キーで運ぶ。
const splitCtaClickLogsByContentKind = (logs) => {
  const regular = {};
  const freqInsert = {};
  const feedFolder = {};
  Object.entries(logs || {}).forEach(([key, value]) => {
    if (value?.isFrequencyInsertContent) {
      freqInsert[key] = value;
    } else if (value?.isFeedFolderContent) {
      feedFolder[key] = value;
    } else {
      regular[key] = value;
    }
  });
  return { regular, freqInsert, feedFolder };
};

const buildRawCrossDomainCvPayload = ({
  campaignId,
  mediaId,
  groupId = null,
  mediaAssetType = null,
  isFrequencyInsertContent = false,
  isFeedFolderContent = false,
} = {}) => {
  const normalizedCampaignId = normalizeOptionalValue(campaignId);
  const normalizedMediaId = normalizeOptionalValue(mediaId);
  if (!normalizedCampaignId || !normalizedMediaId) return null;

  const clickedCta = [
    normalizedCampaignId,
    normalizedMediaId,
    normalizeOptionalValue(groupId),
    normalizeOptionalValue(mediaAssetType),
  ];
  // 5番目はコンテンツ種別フラグ。プロモーション=1、フィードフォルダ=2。通常時はpayloadを増やさない
  if (isFrequencyInsertContent) clickedCta.push(1);
  else if (isFeedFolderContent) clickedCta.push(2);

  const payload = {
    x: clickedCta,
  };
  const logs = getCrossDomainCvLogs();
  const compactLogs = {};

  const campaignLogs = compactCampaignClickLogs(logs.campaignClick);
  if (campaignLogs.length > 0) compactLogs.c = campaignLogs;

  const groupLogs = compactGroupClickLogs(logs.groupClick);
  if (groupLogs.length > 0) compactLogs.g = groupLogs;

  const mediaLogs = compactMediaLogs(logs.mediaView);
  if (mediaLogs.length > 0) compactLogs.m = mediaLogs;

  const {
    regular: regularCtaClickLogs,
    freqInsert: freqInsertCtaClickLogs,
    feedFolder: feedFolderCtaClickLogs,
  } = splitCtaClickLogsByContentKind(logs.ctaClick);

  const ctaLogs = compactMediaLogs(regularCtaClickLogs);
  if (ctaLogs.length > 0) compactLogs.a = ctaLogs;

  const freqInsertCtaLogs = compactMediaLogs(freqInsertCtaClickLogs);
  if (freqInsertCtaLogs.length > 0) compactLogs.f = freqInsertCtaLogs;

  const feedFolderCtaLogs = compactMediaLogs(feedFolderCtaClickLogs);
  if (feedFolderCtaLogs.length > 0) compactLogs.k = feedFolderCtaLogs;

  if (Object.keys(compactLogs).length > 0) payload.l = compactLogs;
  return payload;
};

const registerPayloadStringValue = (value, values) => {
  if (typeof value !== 'string') return;
  if (!values.counts.has(value)) values.order.push(value);
  values.counts.set(value, (values.counts.get(value) || 0) + 1);
};

const collectPayloadStringValues = (payload) => {
  const values = {
    counts: new Map(),
    order: [],
  };

  (payload.x || []).forEach((value) => registerPayloadStringValue(value, values));

  const logs = isPlainObject(payload.l) ? payload.l : {};
  (logs.c || []).forEach((value) => registerPayloadStringValue(value, values));
  (logs.g || []).forEach((entry) => {
    if (!Array.isArray(entry)) return;
    registerPayloadStringValue(entry[0], values);
    (entry[1] || []).forEach((value) => registerPayloadStringValue(value, values));
  });
  ['m', 'a', 'f', 'k'].forEach((logKey) => {
    (logs[logKey] || []).forEach((entry) => {
      if (!Array.isArray(entry)) return;
      entry.forEach((value) => registerPayloadStringValue(value, values));
    });
  });

  return values;
};

const buildPayloadWithInternValues = (payload, internValues) => {
  const internIndexByValue = new Map();
  internValues.forEach((value, index) => {
    internIndexByValue.set(value, index);
  });

  const toRefOrValue = (value) => {
    if (value === null || value === undefined) return null;
    if (internIndexByValue.has(value)) return internIndexByValue.get(value);
    return value;
  };

  const compactPayload = {
    i: [...internValues],
    x: payload.x.map(toRefOrValue),
  };
  const logs = {};

  if (Array.isArray(payload.l?.c) && payload.l.c.length > 0) {
    logs.c = payload.l.c.map(toRefOrValue);
  }
  if (Array.isArray(payload.l?.g) && payload.l.g.length > 0) {
    logs.g = payload.l.g.map(([groupId, campaignIds]) => [
      toRefOrValue(groupId),
      campaignIds.map(toRefOrValue),
    ]);
  }
  ['m', 'a', 'f', 'k'].forEach((logKey) => {
    if (Array.isArray(payload.l?.[logKey]) && payload.l[logKey].length > 0) {
      logs[logKey] = payload.l[logKey].map((entry) => entry.map(toRefOrValue));
    }
  });

  if (Object.keys(logs).length > 0) compactPayload.l = logs;
  return compactPayload;
};

const payloadJsonLengthWithInternValues = (payload, internValues) => (
  JSON.stringify(buildPayloadWithInternValues(payload, internValues)).length
);

const selectInternValues = (payload) => {
  const { counts, order } = collectPayloadStringValues(payload);
  let internValues = order.filter((value) => counts.get(value) >= 2);
  let currentLength = payloadJsonLengthWithInternValues(payload, internValues);

  order.forEach((value) => {
    if (internValues.includes(value)) return;

    const candidateInternValues = [...internValues, value];
    const candidateLength = payloadJsonLengthWithInternValues(payload, candidateInternValues);
    if (candidateLength < currentLength) {
      internValues = candidateInternValues;
      currentLength = candidateLength;
    }
  });

  return internValues;
};

const internCrossDomainCvPayload = (payload) => (
  buildPayloadWithInternValues(payload, selectInternValues(payload))
);

export const buildCrossDomainCvPayload = (params = {}, options = {}) => {
  const payload = buildRawCrossDomainCvPayload(params, options);
  return payload ? internCrossDomainCvPayload(payload) : null;
};

const groupPackedMediaLogEntries = (logs) => {
  if (!Array.isArray(logs) || logs.length === 0) return undefined;

  const grouped = [];
  const groupMap = new Map();
  logs.forEach((entry) => {
    if (!Array.isArray(entry)) return;
    const [media, campaign, group, mediaAssetType] = entry;
    const groupKey = JSON.stringify([campaign, group, mediaAssetType]);
    if (!groupMap.has(groupKey)) {
      const groupEntry = [campaign, group, mediaAssetType, []];
      groupMap.set(groupKey, groupEntry);
      grouped.push(groupEntry);
    }
    groupMap.get(groupKey)[3].push(media);
  });

  return grouped.length > 0 ? grouped : undefined;
};

const packCrossDomainCvPayload = (payload) => {
  const packedPayload = [
    payload.i,
    payload.x,
  ];
  const logEntries = [
    payload.l?.c,
    payload.l?.g,
    groupPackedMediaLogEntries(payload.l?.m),
    groupPackedMediaLogEntries(payload.l?.a),
    groupPackedMediaLogEntries(payload.l?.f),
    groupPackedMediaLogEntries(payload.l?.k),
  ];
  let lastLogIndex = logEntries.length - 1;

  while (lastLogIndex >= 0 && !Array.isArray(logEntries[lastLogIndex])) {
    lastLogIndex -= 1;
  }

  for (let i = 0; i <= lastLogIndex; i += 1) {
    packedPayload.push(Array.isArray(logEntries[i]) ? logEntries[i] : null);
  }

  return packedPayload;
};

const encodeInternedPayload = (payload) => (
  encodeCrossDomainCvPayload(packCrossDomainCvPayload(internCrossDomainCvPayload(payload)))
);

const encodePayloadWithinLimit = (payload, maxLen) => {
  try {
    const encodedPayload = encodeInternedPayload(payload);
    if (encodedPayload.length <= maxLen) return encodedPayload;
  } catch {
    // payload生成に失敗した場合はクロスドメインCVパラメータを付与しない
  }

  // URL長の上限を超えた場合はライブラリ側で追加するCVパラメータを全て付与しない
  return null;
};

const buildCrossDomainCvParamWithPayload = (params, {
  maxLen = STOREEEL_CV_MAX_LEN,
} = {}) => {
  try {
    const payload = buildRawCrossDomainCvPayload(params);
    if (!payload) return null;
    const encodedPayload = encodePayloadWithinLimit(payload, maxLen);
    if (!encodedPayload) return null;
    return { encodedPayload, payload };
  } catch {
    return null;
  }
};

const buildCrossDomainCvParam = (params, options = {}) => (
  buildCrossDomainCvParamWithPayload(params, options)?.encodedPayload || null
);

const stripCrossDomainCvParamsFromUrl = (url) => {
  url.searchParams.delete(CROSS_DOMAIN_CV_PARAMS.payload);
  return url;
};

export const buildCrossDomainCvUrl = (ctaUrl, {
  campaignId,
  mediaId,
  groupId = null,
  mediaAssetType = null,
  isFrequencyInsertContent = false,
  isFeedFolderContent = false,
  maxLen = STOREEEL_CV_MAX_LEN,
} = {}) => {
  if (!ctaUrl || !campaignId || !mediaId) return ctaUrl;

  try {
    const url = new URL(ctaUrl, window.location.href);
    if (!['http:', 'https:'].includes(url.protocol)) return ctaUrl;

    stripCrossDomainCvParamsFromUrl(url);
    const encodedPayload = buildCrossDomainCvParam({
      campaignId,
      mediaId,
      groupId,
      mediaAssetType,
      isFrequencyInsertContent,
      isFeedFolderContent,
    }, {
      maxLen,
    });

    if (encodedPayload) {
      url.searchParams.set(CROSS_DOMAIN_CV_PARAMS.payload, encodedPayload);
    }

    return url.toString();
  } catch {
    return ctaUrl;
  }
};

const buildCrossDomainCvMediaCleanupEntry = (entry) => {
  if (!Array.isArray(entry)) return null;
  const [mediaId, campaignId, groupId] = entry;
  if (!mediaId || !campaignId) return null;
  return { mediaId, campaignId, groupId };
};

const cleanupCrossDomainCvSourceLogs = (payload) => {
  const logs = isPlainObject(payload?.l) ? payload.l : {};

  (logs.c || []).forEach((campaignId) => {
    if (!campaignId) return;
    localStorageRepositories.get('campaignClick')?.remove?.(campaignId);
  });

  (logs.g || []).forEach((entry) => {
    if (!Array.isArray(entry)) return;
    const [groupId, campaignIds] = entry;
    if (!groupId || !Array.isArray(campaignIds)) return;
    campaignIds.forEach((campaignId) => {
      if (!campaignId) return;
      localStorageRepositories.get('groupClick')?.remove?.(groupId, campaignId);
    });
  });

  (logs.m || []).forEach((entry) => {
    const cleanupEntry = buildCrossDomainCvMediaCleanupEntry(entry);
    if (!cleanupEntry) return;
    localStorageRepositories.get('mediaView')?.remove?.(
      cleanupEntry.mediaId,
      cleanupEntry.campaignId,
      cleanupEntry.groupId
    );
  });

  (logs.a || []).forEach((entry) => {
    const cleanupEntry = buildCrossDomainCvMediaCleanupEntry(entry);
    if (!cleanupEntry) return;
    localStorageRepositories.get('ctaClick')?.remove?.(
      cleanupEntry.mediaId,
      cleanupEntry.campaignId,
      cleanupEntry.groupId
    );
  });

  // プロモーション(f)のCTAログは "fi:" プレフィックス付きキーで保存されているため fi 指定で削除する
  (logs.f || []).forEach((entry) => {
    const cleanupEntry = buildCrossDomainCvMediaCleanupEntry(entry);
    if (!cleanupEntry) return;
    localStorageRepositories.get('ctaClick')?.remove?.(
      cleanupEntry.mediaId,
      cleanupEntry.campaignId,
      cleanupEntry.groupId,
      true
    );
  });

  // フィードフォルダ(k)のCTAログは "ff:" プレフィックス付きキーで保存されているため ff 指定で削除する
  (logs.k || []).forEach((entry) => {
    const cleanupEntry = buildCrossDomainCvMediaCleanupEntry(entry);
    if (!cleanupEntry) return;
    localStorageRepositories.get('ctaClick')?.remove?.(
      cleanupEntry.mediaId,
      cleanupEntry.campaignId,
      cleanupEntry.groupId,
      false,
      true
    );
  });

  // xは遷移先でctaClickとして復元されるため、生成元では同じCTAログを削除する
  if (Array.isArray(payload?.x)) {
    const [campaignId, mediaId, groupId] = payload.x;
    const isFrequencyInsertContent = payload.x[4] === 1 || payload.x[4] === true;
    const isFeedFolderContent = payload.x[4] === 2;
    if (campaignId && mediaId) {
      // 通常コンテンツは従来通り3引数で呼び、プロモーション/フィードフォルダ時のみ fi/ff 指定で削除する
      const removeArgs = [mediaId, campaignId, groupId];
      if (isFrequencyInsertContent) removeArgs.push(true);
      else if (isFeedFolderContent) removeArgs.push(false, true);
      localStorageRepositories.get('ctaClick')?.remove?.(...removeArgs);
    }
  }
};

export const buildCrossDomainCvNavigation = (ctaUrl, {
  campaignId,
  mediaId,
  groupId = null,
  mediaAssetType = null,
  isFrequencyInsertContent = false,
  isFeedFolderContent = false,
  maxLen = STOREEEL_CV_MAX_LEN,
} = {}) => {
  const fallback = {
    url: ctaUrl,
    cleanupSourceLogs: () => {},
  };
  if (!ctaUrl || !campaignId || !mediaId) return fallback;

  try {
    const url = new URL(ctaUrl, window.location.href);
    if (!['http:', 'https:'].includes(url.protocol)) return fallback;

    stripCrossDomainCvParamsFromUrl(url);
    const cvParam = buildCrossDomainCvParamWithPayload({
      campaignId,
      mediaId,
      groupId,
      mediaAssetType,
      isFrequencyInsertContent,
      isFeedFolderContent,
    }, {
      maxLen,
    });

    if (!cvParam) {
      return {
        ...fallback,
        url: url.toString(),
      };
    }

    url.searchParams.set(CROSS_DOMAIN_CV_PARAMS.payload, cvParam.encodedPayload);

    return {
      url: url.toString(),
      cleanupSourceLogs: () => {
        try {
          cleanupCrossDomainCvSourceLogs(cvParam.payload);
        } catch {
          // localStorage削除に失敗してもCTA遷移自体は継続する
        }
      },
    };
  } catch {
    return fallback;
  }
};

const resolvePayloadRefOrValue = (value, internValues, { optional = false } = {}) => {
  if (value === null || value === undefined) {
    return optional ? { valid: true, value: null } : { valid: false, value: null };
  }
  if (typeof value === 'string') {
    const normalizedValue = normalizeOptionalValue(value);
    return normalizedValue
      ? { valid: true, value: normalizedValue }
      : { valid: optional, value: null };
  }
  if (Number.isInteger(value) && value >= 0 && value < internValues.length) {
    const normalizedValue = normalizeOptionalValue(internValues[value]);
    return normalizedValue
      ? { valid: true, value: normalizedValue }
      : { valid: false, value: null };
  }

  return { valid: false, value: null };
};

const restoreCampaignClickPayloadLogs = (logs, internValues) => {
  if (!Array.isArray(logs)) return 0;

  let count = 0;
  logs.forEach((campaignRef) => {
    const campaign = resolvePayloadRefOrValue(campaignRef, internValues);
    if (!campaign.valid) return;
    localStorageRepositories.get('campaignClick').set(campaign.value);
    count += 1;
  });
  return count;
};

const restoreGroupClickPayloadLogs = (logs, internValues) => {
  if (!Array.isArray(logs)) return 0;

  let count = 0;
  logs.forEach((entry) => {
    if (!Array.isArray(entry)) return;
    const group = resolvePayloadRefOrValue(entry[0], internValues);
    const campaignIds = entry[1];
    if (!group.valid || !Array.isArray(campaignIds)) return;

    campaignIds.forEach((campaignRef) => {
      const campaign = resolvePayloadRefOrValue(campaignRef, internValues);
      if (!campaign.valid) return;
      localStorageRepositories.get('groupClick').set(group.value, campaign.value);
      count += 1;
    });
  });
  return count;
};

const restoreMediaPayloadLogs = (keyName, logs, internValues, {
  isFrequencyInsertContent = false,
  isFeedFolderContent = false,
} = {}) => {
  if (!Array.isArray(logs)) return 0;

  let count = 0;
  logs.forEach((entry) => {
    if (!Array.isArray(entry)) return;
    const media = resolvePayloadRefOrValue(entry[0], internValues);
    const campaign = resolvePayloadRefOrValue(entry[1], internValues);
    const group = resolvePayloadRefOrValue(entry[2], internValues, { optional: true });
    const mediaAssetType = resolvePayloadRefOrValue(entry[3], internValues, { optional: true });

    if (!media.valid || !campaign.valid || !group.valid || !mediaAssetType.valid) return;
    // ctaClick のプロモーション/フィードフォルダログはそれぞれ isFrequencyInsertContent=true /
    // isFeedFolderContent=true で保存する（CV計測で振り分けられる）
    const setArgs = [media.value, campaign.value, group.value, mediaAssetType.value];
    if (isFrequencyInsertContent) {
      setArgs.push(true);
    } else if (isFeedFolderContent) {
      setArgs.push(false, true);
    }
    localStorageRepositories.get(keyName).set(...setArgs);
    count += 1;
  });
  return count;
};

const restoreCtaClickPayload = (entry, internValues) => {
  if (!Array.isArray(entry)) return 0;

  const campaign = resolvePayloadRefOrValue(entry[0], internValues);
  const media = resolvePayloadRefOrValue(entry[1], internValues);
  const group = resolvePayloadRefOrValue(entry[2], internValues, { optional: true });
  const mediaAssetType = resolvePayloadRefOrValue(entry[3], internValues, { optional: true });
  if (!campaign.valid || !media.valid || !group.valid || !mediaAssetType.valid) return 0;

  // 5番目はコンテンツ種別フラグ。プロモーション=1、フィードフォルダ=2。参照解決せず生値で判定する。
  const isFrequencyInsertContent = entry[4] === 1 || entry[4] === true;
  const isFeedFolderContent = entry[4] === 2;

  // 通常コンテンツは従来通り4引数で呼び、プロモーション/フィードフォルダ時のみフラグを付けて保存する
  const setArgs = [media.value, campaign.value, group.value, mediaAssetType.value];
  if (isFrequencyInsertContent) {
    setArgs.push(true);
  } else if (isFeedFolderContent) {
    setArgs.push(false, true);
  }
  localStorageRepositories.get('ctaClick').set(...setArgs);
  return 1;
};

const restoreCrossDomainCvPayload = (encodedPayload) => {
  try {
    const payload = decodeCrossDomainCvPayload(encodedPayload);
    if (
      !isPlainObject(payload)
      || !Array.isArray(payload.i)
      || !Array.isArray(payload.x)
      || !payload.i.every((value) => typeof value === 'string')
    ) {
      return false;
    }

    let restoredCount = 0;
    const internValues = payload.i;
    const logs = isPlainObject(payload.l) ? payload.l : {};
    restoredCount += restoreCampaignClickPayloadLogs(logs.c, internValues);
    restoredCount += restoreGroupClickPayloadLogs(logs.g, internValues);
    restoredCount += restoreMediaPayloadLogs('mediaView', logs.m, internValues);
    restoredCount += restoreMediaPayloadLogs('ctaClick', logs.a, internValues);
    restoredCount += restoreMediaPayloadLogs('ctaClick', logs.f, internValues, { isFrequencyInsertContent: true });
    restoredCount += restoreMediaPayloadLogs('ctaClick', logs.k, internValues, { isFeedFolderContent: true });
    restoredCount += restoreCtaClickPayload(payload.x, internValues);

    return restoredCount > 0;
  } catch {
    return false;
  }
};

export const stripCrossDomainCvParams = (inputUrl = window.location.href) => {
  const url = new URL(inputUrl, window.location.href);
  return stripCrossDomainCvParamsFromUrl(url);
};

const cleanupCrossDomainCvParamsFromCurrentUrl = () => {
  try {
    const cleanedUrl = stripCrossDomainCvParams(window.location.href).toString();
    if (cleanedUrl === window.location.href) return;
    window.history?.replaceState?.(window.history.state, document.title, cleanedUrl);
  } catch {
    // URLクリーンアップに失敗してもCVログ復元自体は完了扱いにする
  }
};

export const saveCrossDomainCvParamsToLocalStorage = (
  search = window.location.search
) => {
  try {
    const params = new URLSearchParams(search);
    let saved = false;

    if (params.has(CROSS_DOMAIN_CV_PARAMS.payload)) {
      saved = restoreCrossDomainCvPayload(params.get(CROSS_DOMAIN_CV_PARAMS.payload));
    }
    if (saved) {
      cleanupCrossDomainCvParamsFromCurrentUrl();
    }
    return saved;
  } catch {
    return false;
  }
};

export const buildCrossDomainCvCurrentUrl = (inputUrl = window.location.href) => {
  try {
    return stripCrossDomainCvParams(inputUrl).toString();
  } catch {
    return inputUrl;
  }
};
